import { api } from '../lib/api';
import type { FicheClient } from '../lib/contrat';
import { useRequete } from '../lib/requete';
import { canal, pourcentage } from '../lib/format';
import { Badge, Bandeau, ErreurAffichee, Score, Section, Squelette, Vide } from './ui';
import { BandeauFraicheur } from './Fraicheur';
import { Chiffres, chiffresAffaire } from './Chiffres';

/**
 * Fiche consolidée d'un groupe, dépliée sous le contexte de la conversation.
 * `groupeId` est toujours celui fourni par `rattachement.groupe_id`.
 */
export function FicheClientDepliee({ groupeId }: { groupeId: string }) {
  const { donnees, erreur, chargement, rafraichir } = useRequete<FicheClient>(
    (signal, forcer) => api.client(groupeId, signal, forcer),
    [groupeId],
  );

  if (chargement) return <Squelette lignes={5} />;
  if (erreur) return <ErreurAffichee erreur={erreur} onReessayer={rafraichir} />;
  if (!donnees) return <Vide>Fiche indisponible.</Vide>;

  const { chiffres, front, entites, affaires } = donnees;

  return (
    <div className="fiche pile">
      {/* La fiche client a sa propre fraîcheur : elle peut différer de celle
          du contexte de conversation. */}
      <BandeauFraicheur fraicheur={donnees.fraicheur} />

      <Section titre={donnees.nom} repliable cle="fiche.tete">
        <Chiffres
          elements={[
            ...chiffresAffaire(chiffres),
            {
              libelle: 'Transformation',
              valeur: pourcentage(chiffres.taux_transformation),
              aide: 'Part du chiffré qui devient commande',
            },
            {
              libelle: 'Projets',
              valeur: `${chiffres.nb_projets} (${chiffres.nb_projets_ouverts} ouvert${
                chiffres.nb_projets_ouverts > 1 ? 's' : ''
              })`,
            },
            {
              libelle: 'Échanges Front',
              valeur: `${front.conversations} conv. · ${front.interlocuteurs} contacts`,
              aide: `${front.fils} fils de discussion`,
            },
          ]}
        />
      </Section>

      {typeof donnees.score_reprise === 'number' && <Score valeur={donnees.score_reprise} />}

      {donnees.bilan ? (
        <Section titre="Bilan du groupe" repliable cle="fiche.bilan">
          <p className="bilan">{donnees.bilan}</p>
        </Section>
      ) : (
        <Bandeau ton="info" titre="Bilan du groupe pas encore produit">
          Les chiffres et les affaires ci-dessus viennent de Dolibarr. La synthèse
          des échanges viendra avec la phase D du pipeline.
        </Bandeau>
      )}

      {entites.length > 1 && (
        <Section
          titre="Entités du groupe"
          aide="Toutes entités Dolibarr confondues."
          repliable
          cle="fiche.entites"
          ouvertParDefaut={false}
        >
          <ul className="liste-plate">
            {entites.map((entite) => (
              <li key={entite.tiers_id}>
                {entite.nom}
                {entite.ville && <span className="secondaire"> — {entite.ville}</span>}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {affaires.length > 0 && (
        <Section titre="Affaires" repliable cle="fiche.affaires" ouvertParDefaut={false}>
          <ul className="affaires">
            {[...affaires]
              .sort((a, b) => (b.score ?? 0) - (a.score ?? 0))
              .map((affaire, index) => (
                <li key={affaire.projet_ref ?? `${affaire.titre}-${index}`} className="affaire">
                  <div className="affaire-tete">
                    <strong>{affaire.titre}</strong>
                    <Badge
                      ton={affaire.score >= 70 ? 'critique' : affaire.score >= 40 ? 'attention' : 'neutre'}
                    >
                      {affaire.score}
                    </Badge>
                  </div>
                  <p className="affaire-statut">
                    {affaire.statut}
                    <span className="secondaire"> · {canal(affaire.canal)}</span>
                    {affaire.projet_ref && <span className="secondaire"> · {affaire.projet_ref}</span>}
                  </p>
                  <p className="affaire-action">{affaire.action}</p>
                </li>
              ))}
          </ul>
        </Section>
      )}
    </div>
  );
}
